/**
 * Classification Metrics Index Page
 *
 * Dashboard showing classification performance statistics.
 * Displays automation rate, breakdown by method, and suggested rules stats.
 *
 * Features:
 * - Metrics dashboard with visual breakdown
 * - Link to suggested rules review
 * - Loading skeleton during data fetch
 * - Error handling
 */

import { useEffect } from 'react';
import { Head, Link } from '@inertiajs/react';
import { Toaster, toast } from 'sonner';
import { BarChart3, ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import { Navbar } from '@/components';

import { useClassificationMetrics } from '@/hooks/useClassificationMetrics';
import { MetricsDashboard } from './components/MetricsDashboard';

/**
 * ClassificationIndex is the main page for viewing classification metrics.
 */
export default function ClassificationIndex() {
  const { metrics, loading, error, fetchMetrics } = useClassificationMetrics();

  // Fetch metrics on mount
  useEffect(() => {
    fetchMetrics().catch(() => {
      // Error is handled by the hook
    });
  }, [fetchMetrics]);

  // Show error toast on error
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  return (
    <>
      <Head title="Classification Metrics" />
      <Toaster position="top-right" richColors closeButton />

      <div className="min-h-screen bg-gray-50" data-testid="classification-page">
        <Navbar currentSection="accounting" />

        {/* Header */}
        <header className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                href="/accounting/transactions"
                className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft size={16} />
                Transactions
              </Link>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="text-blue-500" size={24} />
                  Classification Metrics
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Track automation performance and classification statistics
                </p>
              </div>
            </div>
            <nav className="flex items-center gap-2">
              <Link
                href="/accounting/suggested_rules"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg"
              >
                <Sparkles size={16} />
                Review Rules
                {metrics && metrics.suggestedRulesPending > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-purple-600 text-white">
                    {metrics.suggestedRulesPending}
                  </span>
                )}
              </Link>
            </nav>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-6 py-6 max-w-6xl mx-auto">
          {/* Error State */}
          {error && !loading && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <svg
                className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-red-800">Error Loading Metrics</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={() => fetchMetrics()}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && !metrics && (
            <div className="space-y-6">
              {/* Automation Rate Skeleton */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 animate-pulse">
                <div className="h-4 bg-green-200 rounded w-32 mb-3" />
                <div className="h-12 bg-green-200 rounded w-24 mb-2" />
                <div className="h-4 bg-green-200 rounded w-48" />
              </div>

              {/* Breakdown Skeleton */}
              <div>
                <div className="h-4 bg-gray-200 rounded w-48 mb-3" />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg" />
                        <div className="flex-1">
                          <div className="h-3 bg-gray-200 rounded w-16 mb-2" />
                          <div className="h-6 bg-gray-200 rounded w-12 mb-1" />
                          <div className="h-3 bg-gray-200 rounded w-10" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Performance Skeleton */}
              <div>
                <div className="h-4 bg-gray-200 rounded w-32 mb-3" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg" />
                        <div className="flex-1">
                          <div className="h-3 bg-gray-200 rounded w-20 mb-2" />
                          <div className="h-6 bg-gray-200 rounded w-16" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Loading indicator for refresh */}
          {loading && metrics && (
            <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" />
              Refreshing...
            </div>
          )}

          {/* Metrics Dashboard */}
          {metrics && <MetricsDashboard metrics={metrics} />}

          {/* Empty State - No Transactions */}
          {!loading && metrics && metrics.totalTransactions === 0 && (
            <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-12 text-center">
                <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  No Transaction Data Yet
                </h2>
                <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
                  Import some transactions to start seeing classification metrics.
                </p>
                <Link
                  href="/accounting/import"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Configure Import
                </Link>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          {metrics && metrics.totalTransactions > 0 && (
            <div className="mt-8 bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                Quick Actions
              </h3>
              <div className="flex flex-wrap gap-2">
                {metrics.needsReview > 0 && (
                  <Link
                    href="/accounting/transactions?status=needs_review"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg"
                  >
                    Review {metrics.needsReview} transaction{metrics.needsReview !== 1 ? 's' : ''}
                  </Link>
                )}
                {metrics.pending > 0 && (
                  <Link
                    href="/accounting/transactions?status=pending"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    View {metrics.pending} pending transaction{metrics.pending !== 1 ? 's' : ''}
                  </Link>
                )}
                {metrics.suggestedRulesPending > 0 && (
                  <Link
                    href="/accounting/suggested_rules"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg"
                  >
                    <Sparkles size={14} />
                    Review {metrics.suggestedRulesPending} suggested rule{metrics.suggestedRulesPending !== 1 ? 's' : ''}
                  </Link>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
